/*
 * Local snapshot of types extracted from the *-dynamodb packages. There is
 * no automated sync — when an upstream schema changes (new field, new enum
 * value, renamed property), update this file by hand. Source versions at
 * the time of extraction (MPR-964):
 *
 *   @mapit/core-dynamodb         3.5.0
 *   @muvio/contacts-dynamodb     2.2.2
 *   @muvio/dealer-bikes-dynamodb 2.0.8
 *   @muvio/honda-catalog-dynamodb 3.0.1
 *   @muvio/honda-offers-dynamodb 2.2.4
 *   @muvio/honda-stock-dynamodb  1.0.0
 */
import type {Moment} from 'moment'

export interface Ref {
  id: string
}

export interface Taggable {
  tags?: string[]
}

export interface Audit {
  createdAt?: Moment
  updatedAt?: Moment
  createdBy?: string
  updatedBy?: string
  silentUpdatedAt?: Moment
}

export enum DocumentType {
  NIF = 'NIF',
  Other = 'Other',
}

export interface AccountDocument {
  type: DocumentType
  number: string
}

export interface PostalAddress {
  streetAddress: string
  addressNumber?: string
  addressLocality: string
  addressRegion?: string
  postalCode: string
  addressCountry: string
  latitude?: string
  longitude?: string
}

export interface AccountAddress {
  streetAddress: string
  addressNumber?: string
  addressLocality: string
  addressRegion?: string
  postalCode: string
  country: string
}

export interface OriginalPaymentMethod {
  stripeSourceId: string
}

export interface PersonalDetails {
  email?: string
  firstName?: string
  lastName?: string
  document?: AccountDocument
  phone?: string
  address?: AccountAddress
  dob?: string
}

export interface AccountInput extends PersonalDetails {
  cognitoSub?: string
  role?: string
  preferences?: Record<string, unknown>
  originalPaymentMethod?: OriginalPaymentMethod
}

export enum Product {
  HondaPlus = 'HP',
  HondaPlusGo = 'HPG',
  HondaMapit = 'HM',
  Finance = 'FIN',
  Insurance = 'INS',
  InsuranceComprehensive = 'INS_COM',
  MapitConnect = 'MC',
}

export enum RegistrationOrigin {
  Sale = 'SALE',
  Resale = 'RESALE',
  Self = 'SELF',
}

export enum RegistrationResolutionStatus {
  ActionsNeeded = 'ACTIONS_NEEDED',
  Finished = 'FINISHED',
}

export enum AttachmentReason {
  NewRegistration = 'NEW_REGISTRATION',
  NewDeviceTransfer = 'NEW_DEVICE_TRANSFER',
  AfterRegistrationAuthorised = 'AFTER_REGISTRATION_AUTHORISED',
  AdditionalServicePurchase = 'ADDITIONAL_SERVICE_PURCHASE',
  AddedConnect = 'ADDED_CONNECT',
  Other = 'ADDED_OTHER',
}

export enum DetachmentReason {
  Voluntary = 'REMOVED_VOLUNTARY',
  Broken = 'REMOVED_BROKEN',
  NoWarranty = 'REMOVED_NO_WARRANTY',
  RegistrationDeleted = 'REGISTRATION_DELETED',
  Other = 'REMOVED_OTHER',
  RemovedConnect = 'REMOVED_CONNECT',
}

export enum PhysicalState {
  NotInstalled = 'NOT INSTALLED',
  Installed = 'INSTALLED',
  Removed = 'REMOVED',
}

export enum LocationState {
  Client = 'CLIENT',
  Warehouse = 'WAREHOUSE',
  User = 'USER',
  Office = 'OFFICE',
  Factory = 'FACTORY',
}

export enum DeviceState {
  ToTest = 'TO TEST',
  TestOK = 'TEST OK',
  TestKO = 'TEST KO',
  OK = 'OK',
  Checking = 'checking',
  KO = 'KO',
}

export enum SleepMode {
  Active = 'ACTIVE',
  Automatic = 'AUTOMATIC',
  Efficiency = 'EFFICIENCY',
}

export enum SimProvider {
  Orange = 'Orange',
  Vodafone = 'Vodafone',
  Tele2 = 'Tele2',
}

export interface DeviceSim {
  line: string
  lineStatus?: string
  lineStatusChangedAt?: string
  imsi: string
  iccid: string
  provider?: SimProvider
  providerStatus?: string
}

export interface VehicleRef extends Ref {
  registrationNumber?: string
}

export interface DeviceSettingsState<V> {
  value: V
  updatedAt: string
}

export interface DeviceSettingsItem<V> {
  current?: DeviceSettingsState<V>
  requested?: DeviceSettingsState<V>
}

export interface DeviceSettings {
  sleepMode?: DeviceSettingsItem<SleepMode>
  towMode?: DeviceSettingsItem<boolean>
  commsCheck?: DeviceSettingsItem<string>
  fallDetection?: DeviceSettingsItem<boolean>
}

export interface DeviceCapability {
  capable: boolean
  options?: SleepMode[]
}

export interface DeviceCapabilities {
  sleepMode: DeviceCapability
  towMode: DeviceCapability
  fallDetection: DeviceCapability
  ignitionOnDetection: DeviceCapability
}

export interface DeviceSaleDetails {
  date?: Moment
  products: Product[]
  source: Ref
}

export interface WarrantyPeriod {
  startDate: string
  endDate: string
}

export interface BoxRef extends Ref {
  name?: string
}

export interface MapitDeviceInput {
  imei: string
  model: string
  vehicle?: VehicleRef
  sim: DeviceSim
  sn: string
  battery?: number
  odometer?: number
  legacyImport?: boolean
  legacyImportAt?: Moment
}

export interface MapitDevice extends MapitDeviceInput, Taggable {
  id: string
  deviceImport?: Ref
  box?: BoxRef
  virtual: boolean
  physicalState?: PhysicalState
  locationState?: LocationState
  deviceState?: DeviceState
  attachmentReason?: AttachmentReason
  detachmentReason?: DetachmentReason
  detachedSubscriptionId?: string
  settings?: DeviceSettings
  capabilities?: DeviceCapabilities
  firstCommAt?: Moment
  saleDetails?: DeviceSaleDetails
  baseWarranty?: WarrantyPeriod
  extendedWarranty?: WarrantyPeriod
  createdAt: Moment
  updatedAt?: Moment
  updatedBy?: string
  /**
   * Set by GET /v1/devices?imei= when a registration that has not completed yet holds this device.
   * A flag rather than the registration itself: that endpoint is open to any dealership.
   */
  heldByPendingRegistration?: boolean
}

export enum SubscriptionStatus {
  Active = 'ACTIVE',
  Cancelled = 'CANCELLED',
}

export interface AccountRef {
  id: string
  email?: string
}

export type StripeSubscriptionObject = Record<string, unknown> & {
  metadata?: Record<string, string> & {reactivationId?: string}
  current_period_end?: number
  current_period_start?: number
}

export interface Subscription {
  id: string
  status: SubscriptionStatus
  periodEnd: Moment
  stripeId: string
  stripeObject: StripeSubscriptionObject
  stripeApiVersion?: string
  cancelAtEnd: boolean
  vehicle: VehicleRef
  account?: AccountRef
  note?: string
  disputed?: boolean
  disputedAt?: Moment
  createdAt: Moment
  updatedAt?: Moment
  legacyImport?: boolean
  legacyImportAt?: Moment
  crmDealId?: string
  paymentInProcess?: boolean
}

export enum DeviceTransferStatus {
  Pending = 'PENDING',
  Completed = 'COMPLETED',
  Created = 'CREATED',
  Expired = 'EXPIRED',
  Rejected = 'REJECTED',
}

export interface ContactPoint {
  name: string
  email?: string
}

export interface DealerLocation {
  name: string
  telephone?: string
  openingHours: string[]
  address?: PostalAddress
  contactPoint?: ContactPoint
  email: string
}

export interface DealerAppointmentConditions {
  holidays: string[]
  daysBeforeRequest: number
  daysBeforeRequestWithCourtesyBike: number
  appointmentsCapacityPerSlot?: number
}

export interface DealerProfile {
  garage: DealerLocation
  shop: DealerLocation
  contactPoint: ContactPoint
  image: string
  logoS3Key: string
  logoAppS3Key?: string
  headerS3Key?: string
  appointmentConditions: DealerAppointmentConditions
  hondaMapitAccount: string
}

export interface DealerLegacy {
  id?: number
  detail: Record<string, unknown>
}

export interface DealerInput {
  name: string
  code: string
  zone: string
  branch: string
  ccaa?: string
  country: string
  locale: string
  profile: DealerProfile
  mktId?: string
  legacy?: DealerLegacy
  imported?: boolean
  importedAt?: Moment
}

export interface Dealer extends Taggable, DealerInput {
  id: string
  createdAt: Moment
  updatedAt?: Moment
}

/* ##########################################
 * ###  @muvio/contacts-dynamodb           ###
 * ##########################################  */

// Was `Address` upstream; renamed to avoid collision with the core-dynamodb Address shape.
export interface MuvioContactAddress {
  addressCountry: string
  addressLocality?: string
  addressNumber?: string
  addressRegion: string
  postalCode?: string
  streetAddress?: string
}

export enum DrivingLicense {
  B = 'B',
  A = 'A',
  A1 = 'A1',
  A2 = 'A2',
}

export interface DrivingLicenseData {
  type: DrivingLicense
  expeditedAt?: Moment
}

export interface ContactInput {
  firstName: string
  lastName: string
  address: MuvioContactAddress
  dob?: string
  phone?: string
  email?: string
  gdprConsent?: boolean
  gdprConsentAt?: Moment
  license?: DrivingLicenseData
}

export interface Contact extends ContactInput, Audit, Ref {}

export interface DealersContact extends ContactInput, Audit, Ref {
  dealerId: string
  contactId: string
}

/* ##########################################
 * ###  @muvio/dealer-bikes-dynamodb       ###
 * ##########################################  */

export interface DealerBikeVehicleInput {
  color: string
  license?: DrivingLicense
  model: string
  registrationNumber?: string
  registrationDate?: string
  vin: string
  modelId?: string
}

export interface MaybeRef {
  id?: string
}

export interface DealerBikeVehicle extends DealerBikeVehicleInput, MaybeRef {}

export interface DeviceRef extends Ref {
  imei: string
}

export const DealerBikeStatus = {
  Candidate: 'CANDIDATE',
  Available: 'AVAILABLE',
  Unavailable: 'UNAVAILABLE',
  Retired: 'RETIRED',
} as const
export type DealerBikeStatus = (typeof DealerBikeStatus)[keyof typeof DealerBikeStatus]

export enum UsageType {
  Courtesy = 'COURTESY',
  Demo = 'DEMO',
}

export enum DealerBikeRemovalReason {
  Other = 'OTHER',
  Sold = 'SOLD_BIKE',
}

export interface DamageLogItem {
  timestamp: Moment
  text: string
}

export interface DealerBikeBaseInput {
  device?: DeviceRef
  vehicle: DealerBikeVehicle
}

export interface DealerBikeCreateInput extends Audit, DealerBikeBaseInput {
  dealer: Ref
  usageType?: UsageType
  predelivery?: Ref
}

export interface DealerBike extends DealerBikeCreateInput {
  damageLog?: DamageLogItem[]
  id: string
  loan?: Ref
  loansCounter?: number
  registration?: Ref
  silentUpdatedAt?: Moment
  status: DealerBikeStatus
  retired?: boolean
  retiredAt?: Moment
  retiredReason?: DealerBikeRemovalReason
}

export interface Document {
  key: string
  version?: string
}

export enum SignatureMethod {
  Digital = 'DIGITAL',
  Paper = 'PAPER',
}

export interface DocumentStatus {
  attendedBy: string
  signed: boolean
  signatureMethod?: SignatureMethod
  signedAt?: Moment
}

export enum ProbabilityOfSale {
  High = 'HIGH',
  Low = 'LOW',
}

export interface LoanAssessment {
  probabilityOfSale: ProbabilityOfSale
  comment?: string
}

export interface LoanHandoverInput {
  damageLog?: DamageLogItem[]
}

export interface LoanHandoverDetail extends DocumentStatus, LoanHandoverInput {}

export interface LoanReturnDetail extends DocumentStatus {
  km: number
  assessment: LoanAssessment
  damageLog?: DamageLogItem[]
}

export const LoanStatus = {
  Booked: 'BOOKED',
  Started: 'STARTED',
  HandedOver: 'HANDED_OVER',
  Returned: 'RETURNED',
  Completed: 'COMPLETED',
  Cancelled: 'CANCELLED',
} as const
export type LoanStatus = (typeof LoanStatus)[keyof typeof LoanStatus]

export interface ContactData extends ContactInput, MaybeRef {}

export interface DealerBikeLoanInput extends Ref, DealerBikeBaseInput {}

export interface LoanInput extends Audit {
  dealer: Ref
  dealerBike: DealerBikeLoanInput
  scheduledFor: Moment
  contact: ContactData
  status?: LoanStatus
  handover?: LoanHandoverInput
  usageType: UsageType
}

export interface Loan extends Ref, LoanInput {
  document?: Document
  handover?: LoanHandoverDetail
  return?: LoanReturnDetail
  returnedAt?: Moment
  status: LoanStatus
}

/* ##########################################
 * ###  @muvio/honda-catalog-dynamodb      ###
 * ##########################################  */

export interface Types {
  category: string
  cc: string
  commercialName?: string
  group: string
  name: string
  type: string
  kw: string
}

export interface Color {
  code: string
  description: string
  general: string
}

export interface MaterialModel {
  commercialName: string
  group: string
  name: string
  type: string
  ym: string
  y: number
}

export interface Material {
  color: Color
  idMat: string | number
  mktWebId?: string
  mktModel?: string
  model: MaterialModel
  vin: string
  co2IdRate?: number
}

export interface PriceModel {
  name: string
  type?: string
  ym: string
}

export interface Price {
  amount: number
  deletionIndic?: string
  model: PriceModel
  payTerms?: string
}

export interface Pack {
  additionalInfo: string
  category: string
  description: string
  frt: number
  partNo: string
  price: number
  priceExclVat: number
}

export interface Campaign {
  name: string
  description: string
  campaignInitDate: string
  campaignEndDate: string
  dateInit: string
  dateEnd: string
  model: string
  yearModel: string
  type: string
  color: string
  value: number
}

export type CO2RateValues = Record<string, number>
export type CO2Rates = Record<string, CO2RateValues>

export interface ProductConfig {
  available: boolean
  price: number
}

export interface HPConfig {
  HP: ProductConfig
  HPG?: ProductConfig
}

export enum FinanceType {
  Basic = 'BASIC',
  Options = 'OPTIONS',
}

export type BasicFinanceConfig = {
  depositRate: number
  feeRate: number
  rate: number
}

export type OptionsFinanceConfig = BasicFinanceConfig & {
  finalInstallmentRate: number
}

export type Finance = {
  [FinanceType.Basic]?: BasicFinanceConfig
  [FinanceType.Options]?: OptionsFinanceConfig
}

export enum InsuranceModality {
  ThirdParty = 'THIRD_PARTY',
  Comprehensive = 'COMPREHENSIVE',
  ThirdPartyFireAndTheft = 'THIRD_PARTY_FIRE_AND_THEFT',
}

export interface Range {
  from: number
  to: number
}

export interface AgeRangePrice {
  ageRange: Range
  price: number
}

export interface InsuranceRegionConfig {
  [key: string]: AgeRangePrice[]
}

export type InsuranceConfig = {
  [key in InsuranceModality]?: InsuranceRegionConfig
}

export interface Insurance {
  rates: InsuranceConfig
}

/* ##########################################
 * ###  @muvio/honda-offers-dynamodb       ###
 * ##########################################  */

export enum PredeliveryStatus {
  NotDelivered = 'NotDelivered',
  Delivered = 'Delivered',
}

export interface PredeliveryDealer {
  code: string
}

export interface PredeliveryVehicle {
  idMat: string
  vin?: string
}

export interface PredeliveryInput extends Audit {
  code: string
  orderedAt: Moment
  deliveredAt?: Moment
  status: PredeliveryStatus
  dealer: PredeliveryDealer
  vehicle: PredeliveryVehicle
  country: string
  quotationDate?: Moment
  orderDate?: Moment
  invoiceDate?: Moment
  predeliveryType?: string
  candidate?: boolean
}

export interface Predelivery extends Ref, Audit, PredeliveryInput {}

/* ##########################################
 * ###  @muvio/honda-stock-dynamodb        ###
 * ##########################################  */

export enum OrderStatus {
  Requested = 'REQUESTED',
  InProgress = 'IN_PROGRESS',
  Sent = 'SENT',
  Denied = 'DENIED',
  Cancelled = 'CANCELLED',
  Withdrawn = 'WITHDRAWN',
}
